import { useAuthStore } from '../../store/authStore';
import type { AuthSubsidiary, AuthUser } from '../../lib/authClient';

/**
 * SubsidiaryPickerScreen — Phase 7.5.
 *
 * Shown after a successful /login when the user has 2+ subsidiaries.
 * (1-subsidiary users skip this step automatically — see authStore.login.)
 *
 * Each row in the list is a card-style button. Clicking dispatches
 * chooseSubsidiary(id) which calls /select-subsidiary; the next state
 * lands either on the BranchPickerScreen (if branches exist) or on the
 * EmptySubsidiaryScreen (if not — handled inside BranchPickerScreen).
 */
export function SubsidiaryPickerScreen({
  user,
  subsidiaries,
  busy,
}: {
  user: AuthUser;
  subsidiaries: AuthSubsidiary[];
  busy: boolean;
}): JSX.Element {
  const error = useAuthStore((s) => s.error);
  const chooseSubsidiary = useAuthStore((s) => s.chooseSubsidiary);
  const logout = useAuthStore((s) => s.logout);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        color: 'var(--text)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 460,
          padding: 28,
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <h1 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 600 }}>
            Choose a company
          </h1>
          <p
            style={{
              margin: 0,
              fontSize: 13,
              color: 'var(--text-muted)',
            }}
          >
            Signed in as <strong style={{ color: 'var(--text)' }}>{user.username}</strong>
          </p>
        </div>

        {error && (
          <div
            style={{
              marginBottom: 14,
              padding: '10px 12px',
              background: 'var(--red-light)',
              border: '1px solid var(--red-border)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--red-text)',
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {subsidiaries.map((s) => (
            <li key={s.id} style={{ marginBottom: 10 }}>
              <button
                onClick={() => void chooseSubsidiary(s.id)}
                disabled={busy}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  textAlign: 'left',
                  background: 'var(--bg)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  color: 'var(--text)',
                  fontSize: 15,
                  cursor: busy ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  transition: 'border-color 120ms ease, background 120ms ease',
                }}
                onMouseEnter={(e) => {
                  if (!busy) {
                    (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--primary)';
                  }
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)';
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontWeight: 600 }}>{s.name}</span>
                  {s.code && (
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      Code: {s.code}
                    </span>
                  )}
                </div>
                <span style={{ color: 'var(--text-muted)', fontSize: 18 }}>›</span>
              </button>
            </li>
          ))}
        </ul>

        <button
          onClick={() => logout()}
          disabled={busy}
          style={{
            width: '100%',
            marginTop: 14,
            padding: '10px 12px',
            background: 'transparent',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--text-muted)',
            fontSize: 13,
            cursor: busy ? 'wait' : 'pointer',
          }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
